import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { calculateDiscountPercent, formatMoney } from '@store/shared';
import { useLanguage } from '../context/LanguageContext.jsx';
import { api } from '../services/api-client.js';
import { ErrorMessage } from '../components/ErrorMessage.jsx';
import { LoadingSpinner } from '../components/LoadingSpinner.jsx';

const emptyOrder = { fullName: '', phone: '', city: '', addressLine1: '', instructions: '' };

export function ProductDetailPage() {
  const { slug } = useParams();
  const { language, t } = useLanguage();
  const isArabic = language === 'ar';
  const copy = (fr, ar) => (isArabic ? ar : fr);
  const [product, setProduct] = useState(null);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [imageError, setImageError] = useState(false);
  const [quantity, setQuantity] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [formToken, setFormToken] = useState('');
  const [idempotencyKey, setIdempotencyKey] = useState('');
  const [order, setOrder] = useState(emptyOrder);
  const [submitting, setSubmitting] = useState(false);
  const [orderError, setOrderError] = useState('');
  const [createdOrder, setCreatedOrder] = useState(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    Promise.all([api.getProductBySlug(slug), api.getFormToken()])
      .then(([productResponse, tokenResponse]) => {
        if (!active) return;
        const nextProduct = productResponse.data;
        setProduct(nextProduct);
        const firstVariant = nextProduct.variants?.[0] || null;
        setSelectedVariant(firstVariant);
        setSelectedImage(firstVariant?.image_url || nextProduct.images?.[0]?.image_url || nextProduct.main_image_url || null);
        setImageError(false);
        setFormToken(tokenResponse?.data?.formToken || '');
        setIdempotencyKey(crypto.randomUUID());
      })
      .catch((caught) => active && setError(caught))
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, [slug]);

  if (loading) return <LoadingSpinner />;
  if (error || !product) return <div className="container page-state"><ErrorMessage message={error?.message || t('productNotFound')} /><Link to="/catalogue" className="btn btn-outline">{t('exploreCatalog')}</Link></div>;

  const name = isArabic ? product.name_ar : product.name_fr;
  const description = isArabic ? product.description_ar : product.description_fr;
  const shortDescription = isArabic ? product.short_description_ar : product.short_description_fr;
  const currentPrice = selectedVariant?.sale_price_cents || selectedVariant?.regular_price_cents || product.sale_price_cents || product.regular_price_cents;
  const regularPrice = selectedVariant?.regular_price_cents || product.regular_price_cents;
  const discount = calculateDiscountPercent(regularPrice, currentPrice);
  const outOfStock = product.stock_status === 'outofstock';

  function updateField(event) {
    setOrder((value) => ({ ...value, [event.target.name]: event.target.value }));
  }

  async function submitOrder(event) {
    event.preventDefault();
    setOrderError('');
    if (!formToken) {
      setOrderError(copy('La protection du formulaire n’est pas prête. Rechargez la page.', 'حماية الاستمارة غير جاهزة. أعد تحميل الصفحة.'));
      return;
    }
    setSubmitting(true);
    try {
      const response = await api.createQuickOrder({
        customer: { fullName: order.fullName.trim(), phone: order.phone.trim() },
        shippingAddress: { city: order.city.trim(), district: '', addressLine1: order.addressLine1.trim(), instructions: order.instructions.trim() },
        items: [{ productId: product.id, variantId: selectedVariant?.id || null, quantity }],
        language,
        formToken,
        website: '',
        consent: true,
      }, idempotencyKey);
      setCreatedOrder(response.data);
      sessionStorage.setItem('last_order_number', response.data.orderNumber);
      sessionStorage.setItem('last_tracking_token', response.data.trackingToken);
    } catch (caught) {
      setOrderError(caught.message || copy('La commande n’a pas pu être enregistrée.', 'تعذر تسجيل الطلب.'));
    } finally {
      setSubmitting(false);
    }
  }

  return <div className="container product-detail-page">
    <nav className="breadcrumb"><Link to="/">{t('home')}</Link><span>/</span><Link to="/catalogue">{t('catalog')}</Link><span>/</span><strong>{name}</strong></nav>
    <div className="product-detail-grid">
      <section className="product-gallery">
        <div className="product-main-image">{selectedImage && !imageError ? <img src={selectedImage} alt={name} onError={() => setImageError(true)} /> : <div className="image-empty">{copy('Image indisponible', 'الصورة غير متوفرة')}</div>}</div>
        {product.images?.length > 1 && <div className="product-thumbnails">{product.images.map((image) => <button key={image.id} type="button" className={selectedImage === image.image_url ? 'active' : ''} onClick={() => setSelectedImage(image.image_url)}><img src={image.image_url} alt="" /></button>)}</div>}
      </section>
      <section className="product-purchase">
        {product.sku && <span className="product-reference">{t('reference')} {product.sku}</span>}
        <h1>{name}</h1>
        {shortDescription && <p className="product-lead">{shortDescription}</p>}
        <div className="product-detail-price"><strong>{formatMoney(currentPrice, 'MAD', language)}</strong>{currentPrice < regularPrice && <><del>{formatMoney(regularPrice, 'MAD', language)}</del><span>-{discount}%</span></>}</div>
        {product.variants?.length > 0 && <div className="variant-selector"><label>{t('selectColor')} : <strong>{isArabic ? selectedVariant?.color_label_ar : selectedVariant?.color_label_fr}</strong></label><div>{product.variants.map((variant) => <button key={variant.id} type="button" className={selectedVariant?.id === variant.id ? 'active' : ''} onClick={() => { setSelectedVariant(variant); if (variant.image_url) { setSelectedImage(variant.image_url); setImageError(false); } }}><i style={{ backgroundColor: variant.color_hex }} />{isArabic ? variant.color_label_ar : variant.color_label_fr}</button>)}</div></div>}
        <div className="quick-order-card" id="commande-rapide">
          {createdOrder ? <div className="quick-order-success" role="status"><span className="success-mark">✓</span><div><h2>{copy('Commande bien reçue', 'تمّ استلام الطلب')}</h2><p>{copy(`Commande n°${createdOrder.orderNumber}. Nous vous contacterons pour la confirmation.`, `رقم الطلب ${createdOrder.orderNumber}. سنتواصل معك للتأكيد.`)}</p></div><Link to="/catalogue" className="btn btn-primary">{copy('Voir d’autres produits', 'مشاهدة منتجات أخرى')}</Link></div> : <form onSubmit={submitOrder}>
            <div className="quick-order-heading"><div><p className="section-kicker">{copy('Commande express', 'طلب سريع')}</p><h2>{copy('Commandez directement ici', 'اطلب مباشرة من هنا')}</h2><p>{copy('Paiement à la livraison après vérification du colis.', 'الدفع عند الاستلام بعد معاينة المنتج.')}</p></div><div className="quantity-control"><button type="button" onClick={() => setQuantity((value) => Math.max(1, value - 1))} disabled={quantity === 1}>−</button><strong>{quantity}</strong><button type="button" onClick={() => setQuantity((value) => Math.min(10, value + 1))}>+</button></div></div>
            <div className="quick-order-fields"><label>{copy('Nom et prénom', 'الاسم الكامل')}<input name="fullName" required autoComplete="name" value={order.fullName} onChange={updateField} /></label><label>{copy('Téléphone', 'رقم الهاتف')}<input name="phone" required inputMode="tel" autoComplete="tel" placeholder="06XXXXXXXX" value={order.phone} onChange={updateField} /></label><label>{copy('Ville', 'المدينة')}<input name="city" required autoComplete="address-level2" value={order.city} onChange={updateField} /></label><label className="field-wide">{copy('Adresse de livraison', 'عنوان التوصيل')}<input name="addressLine1" required autoComplete="street-address" value={order.addressLine1} onChange={updateField} /></label><label className="field-wide">{copy('Instruction (facultatif)', 'ملاحظة (اختياري)')}<input name="instructions" value={order.instructions} onChange={updateField} /></label></div>
            <p className="consent-note">{copy('En commandant, vous acceptez de recevoir les messages WhatsApp liés à la confirmation et au suivi de cette commande.', 'عند الطلب، فإنك توافق على تلقي رسائل واتساب المتعلقة بتأكيد هذا الطلب وتتبع حالته.')}</p>
            {orderError && <div className="admin-alert" role="alert">{orderError}</div>}
            <button className="btn btn-primary quick-order-submit" disabled={submitting || outOfStock}>{submitting ? copy('Enregistrement…', 'جاري التسجيل…') : outOfStock ? copy('Rupture de stock', 'نفد المخزون') : `${copy('Commander maintenant', 'اطلب الآن')} — ${formatMoney(currentPrice * quantity, 'MAD', language)}`}</button>
          </form>}
        </div>
        {description && <div className="product-description"><h2>{copy('Description', 'الوصف')}</h2><p>{description}</p></div>}
      </section>
    </div>
  </div>;
}
